"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

export default function MainContent() {
  // State to track the current view type
  const [viewType, setViewType] = useState('column'); // 'list', 'column', or 'map'
  
  // State to track visible items across all categories
  const [visibleItems, setVisibleItems] = useState({
    fastFood: 3,
    hiTea: 2,
    desiFood: 2
  });

  // State to track selected category tab for mobile view
  const [selectedCategory, setSelectedCategory] = useState('fastFood');

  // Categories for display in the UI
  const categories = [
    { name: 'Fast Food', image: '🍔', key: 'fastFood', slug: 'fast-food' },
    { name: 'Hi-Tea & Coffee', image: '☕', key: 'hiTea', slug: 'hi-tea' },
    { name: 'Desi Food', image: '🍛', key: 'desiFood', slug: 'desi-food' }
  ];

  // Sample restaurant data with more items for demonstrating load more functionality
  const restaurants = {
    "fastFood": [
      {
        id: 1,
        name: "Burger King",
        rating: 4.5,
        deliveryTime: "25-35 min",
        priceRange: "RsRs",
        categories: ["Fast Food", "Burgers"],
        image: "🍔",
        bgColor: "bg-amber-400"
      },
      {
        id: 2,
        name: "Pizza Hut",
        rating: 4.2,
        deliveryTime: "30-45 min",
        priceRange: "RsRsRs",
        categories: ["Pizza", "Italian"],
        image: "🍕",
        bgColor: "bg-red-600"
      },
      {
        id: 3,
        name: "KFC",
        rating: 4,
        deliveryTime: "20-30 min",
        priceRange: "RsRs",
        categories: ["Fast Food", "Fried Chicken"],
        image: "🍗",
        bgColor: "bg-amber-400"
      },
      {
        id: 8,
        name: "McDonald's", 
        rating: 4.3,
        deliveryTime: "15-25 min",
        priceRange: "RsRs",
        categories: ["Fast Food", "Burgers"],
        image: "🍔",
        bgColor: "bg-red-500"
      },
      {
        id: 9,
        name: "Subway",
        rating: 4.1,
        deliveryTime: "20-30 min",
        priceRange: "RsRs",
        categories: ["Fast Food", "Sandwiches"],
        image: "🥪",
        bgColor: "bg-green-600"
      },
      {
        id: 10,
        name: "Hardee's",
        rating: 4.4,
        deliveryTime: "25-40 min",
        priceRange: "RsRsRs",
        categories: ["Fast Food", "Burgers"],
        image: "🍔",
        bgColor: "bg-amber-700"
      }
    ],
    "hiTea": [
      {
        id: 4,
        name: "English Tea House",
        rating: 4.7,
        deliveryTime: "15-25 min",
        priceRange: "RsRsRs",
        categories: ["Hi-Tea", "Desserts"],
        image: "🍵",
        bgColor: "bg-green-600"
      },
      {
        id: 5,
        name: "Chai Spot",
        rating: 4.3,
        deliveryTime: "20-30 min",
        priceRange: "Rs",
        categories: ["Hi-Tea", "Local"],
        image: "☕",
        bgColor: "bg-amber-700"
      },
      {
        id: 11,
        name: "Cafe Aylanto",
        rating: 4.6,
        deliveryTime: "25-35 min",
        priceRange: "RsRsRs",
        categories: ["Hi-Tea", "Cafe"],
        image: "🧁",
        bgColor: "bg-pink-500"
      },
      {
        id: 12,
        name: "Gloria Jean's",
        rating: 4.2,
        deliveryTime: "20-30 min",
        priceRange: "RsRs",
        categories: ["Hi-Tea", "Coffee"],
        image: "☕",
        bgColor: "bg-amber-800"
      }
    ],
    "desiFood": [
      {
        id: 6,
        name: "Karachi Biryani",
        rating: 4.6,
        deliveryTime: "25-35 min",
        priceRange: "RsRs",
        categories: ["Desi", "Biryani"],
        image: "🍚",
        bgColor: "bg-orange-500"
      },
      {
        id: 7,
        name: "Lahori Dhaba",
        rating: 4.5,
        deliveryTime: "30-40 min",
        priceRange: "RsRs",
        categories: ["Desi", "Punjabi"],
        image: "🍛",
        bgColor: "bg-red-500"
      },
      {
        id: 13,
        name: "Bundu Khan",
        rating: 4.4,
        deliveryTime: "30-45 min",
        priceRange: "RsRsRs",
        categories: ["Desi", "BBQ"],
        image: "🍖",
        bgColor: "bg-amber-600"
      },
      {
        id: 14,
        name: "Student Biryani",
        rating: 4.2,
        deliveryTime: "25-35 min",
        priceRange: "RsRs",
        categories: ["Desi", "Biryani"],
        image: "🍚",
        bgColor: "bg-yellow-600"
      },
      {
        id: 15,
        name: "Salt'n Pepper",
        rating: 4.7,
        deliveryTime: "35-50 min",
        priceRange: "RsRsRs",
        categories: ["Desi", "Fine Dining"],
        image: "🍲",
        bgColor: "bg-red-700"
      }
    ],
    "healthy": [
      // Healthy food restaurants would go here
    ]
  };

  // Helper function to create URL-friendly slugs from restaurant names
  const createSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-');
  };

  // Handle loading more items
  const handleLoadMore = () => {
    setVisibleItems({
      fastFood: visibleItems.fastFood + 3,
      hiTea: visibleItems.hiTea + 2,
      desiFood: visibleItems.desiFood + 2
    });
  };

  // Check if there are more items to load
  const hasMoreItems = () => {
    return (
      (restaurants.fastFood && restaurants.fastFood.length > visibleItems.fastFood) ||
      (restaurants.hiTea && restaurants.hiTea.length > visibleItems.hiTea) ||
      (restaurants.desiFood && restaurants.desiFood.length > visibleItems.desiFood)
    );
  };

  // Calculate total number of restaurants across all categories
  const getTotalRestaurantCount = () => {
    let total = 0;
    if (restaurants.fastFood) total += restaurants.fastFood.length;
    if (restaurants.hiTea) total += restaurants.hiTea.length;
    if (restaurants.desiFood) total += restaurants.desiFood.length;
    return total;
  };

  // Render restaurant card in list view
  const renderRestaurantCard = (restaurant) => {
    return (
      <div key={restaurant.id} className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4 last:mb-0 overflow-hidden">
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex flex-1">
              <div className={`${restaurant.bgColor} text-white rounded-lg w-12 h-12 flex items-center justify-center text-2xl mr-4 flex-shrink-0`}>
                {restaurant.image}
              </div>
              <div className="flex-1 min-w-0">
                <Link href={`/restaurant/${createSlug(restaurant.name)}`}>
                  <h3 className="font-semibold text-gray-800 mb-1 text-lg">{restaurant.name}</h3>
                </Link>
                <div className="flex items-center text-sm text-gray-500 mb-1.5">
                  <div className="flex items-center mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-400 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    {restaurant.rating}
                  </div>
                  <div className="flex items-center mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {restaurant.deliveryTime}
                  </div>
                  <div className="text-primary font-medium">{restaurant.priceRange}</div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {restaurant.categories.map((cat, idx) => (
                    <span key={idx} className="bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 text-xs">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            <button className="bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-dark transition-colors ml-4 flex-shrink-0">
              Order
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render Category section with its restaurants for list view
  const renderCategorySection = (category) => {
    if (!restaurants[category.key] || restaurants[category.key].length === 0) return null;
    
    const items = restaurants[category.key];
    const visibleCount = visibleItems[category.key] || 3;
    const displayedItems = items.slice(0, visibleCount);

    return (
      <section key={category.key} className="mb-8 last:mb-0">
        {/* Category Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="text-2xl mr-2">{category.image}</div>
            <h2 className="text-xl font-bold text-gray-800">{category.name}</h2>
          </div>
          
          <Link href={`/category/${category.slug}`} className="text-primary font-medium text-sm flex items-center hover:underline">
            View All
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
        
        {/* Restaurant Cards */}
        <div className="space-y-4">
          {displayedItems.map(restaurant => renderRestaurantCard(restaurant))}
        </div>
      </section>
    );
  };

  // Render Column View (smaller cards in columns)
  const renderColumnView = () => {
    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Create three columns, one for each category */}
          {categories.map(category => {
            if (!restaurants[category.key] || restaurants[category.key].length === 0) return null;
            
            const items = restaurants[category.key];
            const visibleCount = visibleItems[category.key] || 3;
            const displayedItems = items.slice(0, visibleCount);
            
            return (
              <div key={category.key} className="flex flex-col space-y-4">
                {/* Category Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-2xl mr-2">{category.image}</div>
                    <h2 className="text-lg font-bold text-gray-800">{category.name}</h2>
                  </div>
                  
                  <Link href={`/category/${category.slug}`} className="text-primary font-medium text-xs flex items-center hover:underline">
                    View All
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </div>
                
                {/* Restaurant Cards - Stacked vertically within the column */}
                {displayedItems.map(restaurant => (
                  <div key={restaurant.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-3">
                      <Link href={`/restaurant/${createSlug(restaurant.name)}`}>
                        <div className="flex items-center">
                          <div className={`${restaurant.bgColor} text-white rounded-lg w-10 h-10 flex items-center justify-center text-xl mr-3 flex-shrink-0`}>
                            {restaurant.image}
                          </div>
                          <h3 className="font-semibold text-gray-800 text-sm">{restaurant.name}</h3>
                        </div>
                      </Link>
                      
                      <div className="mt-2 flex items-center text-xs text-gray-500">
                        <div className="flex items-center mr-2">
                          <span className="text-yellow-400 mr-1">★</span>
                          <span>{restaurant.rating}</span>
                        </div>
                        <span className="mx-1">•</span>
                        <div className="mr-2">{restaurant.deliveryTime}</div>
                        <span className="mx-1">•</span>
                        <div className="text-primary font-medium">{restaurant.priceRange}</div>
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mt-2">
                        {restaurant.categories.map((cat, idx) => (
                          <span key={idx} className="bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 text-xs">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="px-3 pb-3">
                      <button className="w-full bg-primary text-white rounded-md py-1.5 text-xs font-medium hover:bg-primary-dark transition-colors">
                        Order
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        
        {/* Global Load More button for all categories */}
        {hasMoreItems() && (
          <div className="text-center mt-8">
            <button 
              onClick={handleLoadMore}
              className="px-6 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 text-gray-800 font-medium transition-colors"
            >
              Load More
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render List View (vertical list of restaurants by category)
  const renderListView = () => (
    <div>
      {categories.map(category => renderCategorySection(category))}
      
      {/* Global Load More button for all categories */}
      {hasMoreItems() && (
        <div className="text-center mt-8">
          <button 
            onClick={handleLoadMore}
            className="px-6 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 text-gray-800 font-medium transition-colors"
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );

  // Render Map View (placeholder)
  const renderMapView = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden p-8">
      <div className="flex flex-col items-center justify-center text-center h-80">
        <div className="bg-gray-100 p-4 rounded-full mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">Map View Coming Soon</h3>
        <p className="text-gray-500 mb-4 max-w-md">We're working on an interactive map to help you discover restaurants near you. Check back soon!</p>
        <button className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Share My Location
        </button>
      </div>
    </div>
  );

  // Render the mobile category tabs 
  const renderMobileCategoryTabs = () => {
    return (
      <div className="md:hidden mb-6 overflow-x-auto">
        <div className="flex space-x-2 pb-1">
          {categories.map(category => (
            <button
              key={category.key}
              onClick={() => setSelectedCategory(category.key)}
              className={`flex items-center px-4 py-2 rounded-full whitespace-nowrap ${
                selectedCategory === category.key 
                ? 'bg-primary text-white font-medium shadow-sm' 
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span className="mr-1.5">{category.image}</span>
              {category.name}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Render mobile view of restaurants by selected category
  const renderMobileView = () => {
    const category = categories.find(cat => cat.key === selectedCategory);
    if (!category || !restaurants[category.key] || restaurants[category.key].length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No restaurants found in this category.
        </div>
      );
    }

    const items = restaurants[category.key];
    const visibleCount = visibleItems[category.key] || 3;
    const displayedItems = items.slice(0, visibleCount);
    
    return (
      <div className="space-y-4">
        {displayedItems.map(restaurant => renderRestaurantCard(restaurant))}
        
        {/* Category-specific load more button */}
        {items.length > visibleCount && (
          <div className="text-center mt-4">
            <button 
              onClick={() => {
                const updatedVisibleItems = {...visibleItems};
                updatedVisibleItems[category.key] = visibleItems[category.key] + 3;
                setVisibleItems(updatedVisibleItems);
              }}
              className="px-6 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 text-gray-800 font-medium transition-colors text-sm w-full"
            >
              Load More {category.name}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 h-screen overflow-y-auto pt-6 px-6 pb-36 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* Logo Section */}
        <div className="flex justify-center mb-6">
          <div className="w-16 md:w-20">
            <img 
              src="/images/logo-oval.svg" 
              alt="OpenMenu.pk Logo" 
              width={150} 
              height={90} 
              className="w-full h-auto" 
            />
          </div>
        </div>
        
        {/* Header Section - Welcome and View Toggle */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div className="mb-4 md:mb-0">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Find Your Next Meal</h1>
            <p className="text-gray-600 mt-1">Discover the best food in Pakistan</p>
          </div>
          
          {/* View Type Toggle - hide on mobile */}
          <div className="hidden md:flex items-center">
            <div className="text-sm text-gray-500 mr-2">View:</div>
            <div className="flex bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button 
                onClick={() => setViewType('list')} 
                className={`px-3 py-1.5 text-sm flex items-center ${viewType === 'list' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                title="List View"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                List
              </button>
              <button 
                onClick={() => setViewType('column')} 
                className={`px-3 py-1.5 text-sm flex items-center ${viewType === 'column' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                title="Column View"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                Column
              </button>
              <button 
                onClick={() => setViewType('map')} 
                className={`px-3 py-1.5 text-sm flex items-center ${viewType === 'map' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                title="Map View"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Map
              </button>
            </div>
          </div>
        </div>

        {/* Restaurant Count Indicator */}
        <div className="mb-6">
          <div className="text-gray-800 font-medium">
            {getTotalRestaurantCount()} {getTotalRestaurantCount() === 1 ? 'Restaurant' : 'Restaurants'} Found
          </div>
        </div>
        
        {/* Mobile Category Tabs */}
        {renderMobileCategoryTabs()}
        
        {/* Content based on view type */}
        <div className="mb-8">
          {/* Show mobile tab content on mobile, regular content on desktop */}
          <div className="md:hidden">
            {renderMobileView()}
          </div>
          <div className="hidden md:block">
            {viewType === 'list' && renderListView()}
            {viewType === 'column' && renderColumnView()}
            {viewType === 'map' && renderMapView()}
          </div>
        </div>
      </div>
    </div>
  );
} 